import { Matrix, Mesh, SceneLoader, Vector3, type AbstractMesh, type Scene, type TransformNode } from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { createLogger, fromLightSourceProperties, isBreakablePropObject, isLightMarkerName, OBJECT_TYPES, SeededRng, type LightSourceDefinition, type MapBounds, type Vector2 } from '@sorrem/shared';
import type { MapObjectLayer } from '../map-loader';
import type { FlameLightSource } from './crucible-lighting';
import { createBrazierFire, type BrazierFire } from './vfx/brazier-fire';
import { SceneLoadOnceCaches } from './load-once-cache';
import { resolveSceneYaw } from './world-to-scene-position';

const logger = createLogger('engine:map-props');

/** Root URL prop model paths (`propModel`) are relative to. */
const ASSET_ROOT = '/assets/';

export interface MapPropBuildResult {
    readonly meshes: readonly AbstractMesh[];
    /** A breakable prop's mesh (#302), by its map object id, to outline it; undefined when this build has none by that id. */
    findBreakablePropMesh(id: string): AbstractMesh | undefined;
    /** The props hung on a wall, with the wall tile each hangs on, so they can go with a cut wall. */
    readonly wallMounted: readonly WallMountedProp[];
    /** Takes away a prop that broke (#302), by its map object id; false when this build has none by that id. */
    removeProp(id: string): boolean;
    /** Where each standing prop's flame casts its light, for the run's atmosphere to light (#226). */
    flameLights(): readonly FlameLightSource[];
    /** How a prop's flames are placed (#353), by its map object id, for an editor; undefined when this build has none by that id. */
    describePropModel(id: string): PropModelDescription | undefined;
    /** Animates the props for this frame: their fires' light flickers. */
    update(deltaSeconds: number): void;
    dispose(): void;
}

/** How a placed prop's model places its flames (#353), and its model file's coordinates, in which a light source's `origin` is given. */
export interface PropModelDescription {
    /** How many `light` markers the model has: its flames burn there, whatever the light source's `origin`. */
    readonly markerCount: number;
    /** Whether the model has `Fire` geometry, at whose base a flame burns with no marker and no origin. */
    readonly hasFireGeometry: boolean;
    /** The box the model fills, in its model file's coordinates: what an `origin` or mount origin is typed against. */
    readonly modelBounds: { readonly min: Vector3; readonly max: Vector3 };
    /** A point in the scene, on the prop, in its model file's coordinates (an `origin`). */
    toModelPoint(scenePoint: Vector3): Vector3;
    /** A point in the model file's coordinates (an `origin`), where it stands in the scene on this prop. */
    toScenePoint(modelPoint: Vector3): Vector3;
}

/** What a breakable prop's instance carries in `metadata`, so a pick knows which map object it hit. */
interface BreakablePropMeshMetadata {
    readonly breakablePropId: string;
}

/** The map object id of the breakable prop a mesh draws; null for any other mesh. */
export function findBreakablePropId(mesh: AbstractMesh): string | null {
    const metadata = mesh.metadata as Partial<BreakablePropMeshMetadata> | null | undefined;
    return typeof metadata?.breakablePropId === 'string' ? metadata.breakablePropId : null;
}

export interface WallMountedProp {
    readonly instance: AbstractMesh;
    readonly wallTile: Vector2;
    /** The prop's flames, if it burns; their light is meant to stay when the prop is hidden. */
    readonly effects: readonly TransformNode[];
}

interface PropPlacement {
    readonly id: string;
    readonly model: string;
    readonly x: number;
    readonly y: number;
    readonly height: number;
    readonly yawDegrees: number;
    readonly elevation: number;
    readonly isOnWall: boolean;
    /** For a prop hung on a wall: the point of its model (model-file coordinates) that hangs at its place and mount height. */
    readonly mountOrigin?: Vector3;
    /** A player's attack breaks it (#302): its instance can be picked, to be clicked. */
    readonly isBreakable: boolean;
    /** The light it gives off, when it is a light source (#353). */
    readonly light?: LightSourceDefinition;
}

/**
 * Points of a prop model, in its base mesh's own coordinates (see
 * `importBaseMesh`): where its flames burn (its `light` markers, the base
 * of its `Fire` geometry), and how to bring a point given in the model
 * file's coordinates (a light source's `origin`, a mount origin) into
 * the base mesh's.
 */
interface PropModelAnchors {
    readonly markers: readonly Vector3[];
    readonly fireBase: Vector3 | null;
    readonly fromModel: Matrix;
}

// One base mesh per model file and scene, shared by every prop instance in
// the scene (and across its map reloads), the same way wall models are cached.
const baseMeshes = new SceneLoadOnceCaches<string, Mesh>((mesh) => mesh.dispose());
const modelAnchors = new WeakMap<Mesh, PropModelAnchors>();

/**
 * Draws the props a crucible was decorated with at generation time. Each
 * prop object carries its own model, size and facing (see
 * @sorrem/crucible-generation), so no catalog is needed here. Props are
 * instanced per model and not pickable, so clicks fall through to the floor;
 * only breakable props can be picked, to be clicked and broken.
 * With a `region`, only the props standing on its tiles are built.
 */
export async function buildMapProps(
    scene: Scene,
    objectLayers: readonly MapObjectLayer[],
    tileUnitSize: number,
    region?: MapBounds,
): Promise<MapPropBuildResult> {
    const inRegion = (x: number, y: number): boolean => !region
        || (Math.floor(x) >= region.minX && Math.floor(y) >= region.minY && Math.floor(x) < region.maxX && Math.floor(y) < region.maxY);
    const placements = objectLayers.flatMap((layer) => layer.objects)
        .flatMap((object): PropPlacement[] => {
            const { properties } = object;
            const model = properties['propModel'];
            if (object.type !== OBJECT_TYPES.PROP || typeof model !== 'string' || model === '' || !inRegion(object.x, object.y)) {
                return [];
            }
            const read = (key: string, fallback: number): number => {
                const value = Number(properties[key]);
                return Number.isFinite(value) ? value : fallback;
            };
            const onWall = properties['placement'] === 'on-wall';
            const light = fromLightSourceProperties(properties);
            const mountOrigin = [properties['mountOriginX'], properties['mountOriginY'], properties['mountOriginZ']];
            const hasMountOrigin = onWall && mountOrigin.every((value) => typeof value === 'number' && Number.isFinite(value));
            return [{
                id: object.id,
                model,
                x: object.x,
                y: object.y,
                height: read('propHeight', 1),
                yawDegrees: read('rotationDegrees', 0),
                elevation: onWall ? read('wallHeight', 1.2) : 0,
                isOnWall: onWall,
                ...(hasMountOrigin ? { mountOrigin: Vector3.FromArray(mountOrigin as number[]) } : {}),
                isBreakable: isBreakablePropObject(object),
                ...(light ? { light } : {}),
            }];
        });

    const instances: AbstractMesh[] = [];
    const wallMounted: WallMountedProp[] = [];
    // Each prop's own pieces, by map object id, so a broken one goes whole.
    const placed = new Map<string, {
        readonly instance: AbstractMesh;
        readonly base: Mesh;
        readonly fires: readonly BrazierFire[];
        readonly lights: readonly FlameLightSource[];
        readonly wallMounted: WallMountedProp | null;
    }>();
    const models = [...new Set(placements.map((placement) => placement.model))];
    const bases = await Promise.all(models.map(async (model) => {
        try {
            return [model, await loadBaseMesh(scene, model)] as const;
        } catch (error) {
            logger.warn(`Failed to load prop model ${model}:`, error);
            return [model, null] as const;
        }
    }));
    const baseByModel = new Map(bases);

    for (const placement of placements) {
        const base = baseByModel.get(placement.model);
        if (!base) {
            continue;
        }
        // A breakable prop is its own mesh (sharing the model's geometry), not an instance, so it can be
        // outlined alone when hovered, as a chest is; the other props are instances, drawn in one batch.
        const instance = placement.isBreakable ? cloneBreakableProp(base, placement.id) : base.createInstance(`prop-${placement.id}`);
        const modelHeight = base.getBoundingInfo().boundingBox.extendSize.y * 2;
        instance.scaling.setAll(modelHeight > 0 ? placement.height / modelHeight : 1);
        // Tile x maps to scene z and tile y to scene x (see worldSpaceToScenePosition).
        instance.position = new Vector3(placement.y * tileUnitSize, placement.elevation, placement.x * tileUnitSize);
        instance.rotationQuaternion = null;
        instance.rotation.y = resolveSceneYaw(placement.yawDegrees);
        hangByMountOrigin(instance, modelAnchors.get(base), placement);
        instance.isPickable = placement.isBreakable;
        if (placement.isBreakable) {
            instance.metadata = { breakablePropId: placement.id } satisfies BreakablePropMeshMetadata;
        }
        instances.push(instance);

        // TODO(#353): a glow lights the area from the whole model; only flames burn so far.
        const flames = placement.light?.kind === 'flame' && placement.light.flame
            ? resolveFlameBases(instance, modelAnchors.get(base), placement.light).map((position, index) => createFlame(scene, placement, index, position, placement.light!, tileUnitSize))
            : [];
        const fires = flames.map((flame) => flame.fire);
        const mounted = placement.isOnWall ? { instance, wallTile: resolveWallTileBehind(placement), effects: fires.map((fire) => fire.root) } : null;
        if (mounted) {
            wallMounted.push(mounted);
        }
        placed.set(placement.id, { instance, base, fires, lights: flames.map((flame) => flame.light), wallMounted: mounted });
    }

    return {
        meshes: instances,
        wallMounted,
        findBreakablePropMesh: (id: string): AbstractMesh | undefined => {
            const prop = placed.get(id);
            return prop && findBreakablePropId(prop.instance) !== null ? prop.instance : undefined;
        },
        removeProp: (id: string): boolean => {
            const prop = placed.get(id);
            if (!prop) {
                return false;
            }
            placed.delete(id);
            instances.splice(instances.indexOf(prop.instance), 1);
            if (prop.wallMounted) {
                wallMounted.splice(wallMounted.indexOf(prop.wallMounted), 1);
            }
            prop.instance.dispose();
            prop.fires.forEach((fire) => fire.dispose());
            return true;
        },
        flameLights: (): readonly FlameLightSource[] => [...placed.values()].flatMap(({ lights }) => lights),
        describePropModel: (id: string): PropModelDescription | undefined => {
            const prop = placed.get(id);
            const anchors = prop && modelAnchors.get(prop.base);
            return prop && anchors ? describeModelAnchors(prop.instance, prop.base, anchors) : undefined;
        },
        update: (deltaSeconds: number): void => {
            placed.forEach(({ fires }) => fires.forEach((fire) => fire.update(deltaSeconds)));
        },
        dispose: (): void => {
            placed.forEach(({ instance, fires }) => {
                instance.dispose();
                fires.forEach((fire) => fire.dispose());
            });
            placed.clear();
        },
    };
}

/**
 * Moves a wall prop so its mount origin, not its bottom middle, is where it
 * hangs: its place on the wall at its mount height (its position as set).
 */
function hangByMountOrigin(instance: AbstractMesh, anchors: PropModelAnchors | undefined, placement: PropPlacement): void {
    if (!placement.mountOrigin || !anchors) {
        return;
    }
    const hangsAt = instance.position.clone();
    const mount = Vector3.TransformCoordinates(Vector3.TransformCoordinates(placement.mountOrigin, anchors.fromModel), instance.computeWorldMatrix(true));
    instance.position = hangsAt.add(hangsAt.subtract(mount));
    instance.computeWorldMatrix(true);
}

/** The wall tile a wall prop hangs on: half a tile behind it, against the way it faces. */
function resolveWallTileBehind(placement: PropPlacement): Vector2 {
    const radians = (placement.yawDegrees * Math.PI) / 180;
    return {
        x: Math.floor(placement.x - Math.sin(radians) * 0.5),
        y: Math.floor(placement.y + Math.cos(radians) * 0.5),
    };
}

/**
 * Where a placed prop's flames burn, in the scene: at each of its model's
 * `light` markers; else at the light source's `origin` (model file
 * coordinates); else at the base of the model's `Fire` geometry; else at
 * the model's base. Each point is carried with the instance's own size,
 * turn and place, so a flame stays on its model however it is mounted.
 */
export function resolveFlameBases(instance: AbstractMesh, anchors: PropModelAnchors | undefined, light: LightSourceDefinition): Vector3[] {
    const local = anchors?.markers.length ? anchors.markers
        : light.origin && anchors ? [Vector3.TransformCoordinates(Vector3.FromArray([...light.origin]), anchors.fromModel)]
            : anchors?.fireBase ? [anchors.fireBase]
                : [Vector3.Zero()];
    const world = instance.computeWorldMatrix(true);
    return local.map((point) => Vector3.TransformCoordinates(point, world));
}

/** A placed prop's light anchors as an editor reads them; its points are converted through the instance as it stands now. */
function describeModelAnchors(instance: AbstractMesh, base: Mesh, anchors: PropModelAnchors): PropModelDescription {
    // Model file -> base mesh -> scene, and back.
    const toScene = (): Matrix => anchors.fromModel.multiply(instance.computeWorldMatrix(true));
    // The base mesh's box corners, back in the model file (its axes may be flipped or turned there).
    const toModel = Matrix.Invert(anchors.fromModel);
    const corners = base.getBoundingInfo().boundingBox.vectors.map((corner) => Vector3.TransformCoordinates(corner, toModel));
    return {
        markerCount: anchors.markers.length,
        hasFireGeometry: anchors.fireBase !== null,
        modelBounds: {
            min: corners.reduce((least, corner) => Vector3.Minimize(least, corner)),
            max: corners.reduce((most, corner) => Vector3.Maximize(most, corner)),
        },
        toModelPoint: (scenePoint: Vector3): Vector3 => Vector3.TransformCoordinates(scenePoint, Matrix.Invert(toScene())),
        toScenePoint: (modelPoint: Vector3): Vector3 => Vector3.TransformCoordinates(modelPoint, toScene()),
    };
}

/**
 * Where a flame's light hangs: a little up the flame, where a fire's own
 * light would sit; for a prop hung on a wall with a `wallDistance`, moved
 * straight out to that far in front of where it hangs (its place, on the
 * wall or a pilaster's front), kept along the wall and up it.
 */
function resolveFlameLightPosition(placement: PropPlacement, flameBase: Vector3, source: LightSourceDefinition, tileUnitSize: number): Vector3 {
    const position = flameBase.add(new Vector3(0, source.flame!.height * 0.4, 0));
    if (!placement.isOnWall || source.wallDistance === undefined) {
        return position;
    }
    // The way it faces (yaw 0 faces north, -y; 90 east, +x), in the scene: tile x runs along scene z, tile y along scene x.
    const radians = (placement.yawDegrees * Math.PI) / 180;
    const out = new Vector3(-Math.cos(radians), 0, Math.sin(radians));
    const hangsAt = new Vector3(placement.y * tileUnitSize, 0, placement.x * tileUnitSize);
    const alreadyOut = Vector3.Dot(position.subtract(hangsAt), out);
    return position.add(out.scale(source.wallDistance * tileUnitSize - alreadyOut));
}

/**
 * A burning prop's flame at `position` (its base), and where its light
 * hangs (`resolveFlameLightPosition`). The flame
 * itself lights nothing (every torch casting light would blow past Babylon's
 * per-material light limit); the run's atmosphere lights the flames nearest
 * the view (`CrucibleLighting`).
 */
function createFlame(
    scene: Scene,
    placement: PropPlacement,
    index: number,
    position: Vector3,
    source: LightSourceDefinition,
    tileUnitSize: number,
): { readonly fire: BrazierFire; readonly light: FlameLightSource } {
    const size = source.flame!;
    const rng = new SeededRng(index === 0 ? placement.id : `${placement.id}|${index}`);
    const timeOffsetSeconds = rng.random() * 10;
    const flickerPhase = rng.random() * Math.PI * 2;
    const fire = createBrazierFire(scene, {
        position,
        width: size.width,
        height: size.height,
        flameSpeed: 2,
        flameBrightness: 0.55,
        lightIntensity: 0,
        lightRange: 0,
        timeOffsetSeconds,
        flickerPhase,
    });
    return {
        fire,
        light: {
            position: resolveFlameLightPosition(placement, position, source, tileUnitSize),
            flickerPhase,
            rangeTiles: source.rangeTiles,
            ...(source.color !== undefined ? { color: source.color } : {}),
        },
    };
}

/**
 * Loads prop models ahead of the map chunks that place them, so a chunk
 * built later loads nothing. A model that fails to load is skipped (and tried
 * again when a chunk needs it), as when building.
 */
export async function preloadPropModels(scene: Scene, models: readonly string[]): Promise<void> {
    await Promise.all(models.map((model) => loadBaseMesh(scene, model).catch((error: unknown) => {
        logger.warn(`Failed to preload prop model ${model}:`, error);
    })));
}

/** A breakable prop's own copy of its hidden base mesh: the same geometry and material, shown. */
function cloneBreakableProp(base: Mesh, id: string): Mesh {
    const clone = base.clone(`prop-${id}`, null, true);
    clone.isVisible = true;
    return clone;
}

function loadBaseMesh(scene: Scene, model: string): Promise<Mesh> {
    return baseMeshes.of(scene).get(model, () => importBaseMesh(scene, model));
}

/** The middle of the bottom of a model's `Fire` geometry (its material named Fire), in the imported scene; null without one. */
function findFireBase(geometry: readonly Mesh[]): Vector3 | null {
    const fire = geometry.filter((mesh) => /^fire\b/i.test(mesh.material?.name ?? ''));
    if (fire.length === 0) {
        return null;
    }
    const boxes = fire.map((mesh) => {
        mesh.computeWorldMatrix(true);
        mesh.refreshBoundingInfo();
        return mesh.getBoundingInfo().boundingBox;
    });
    const min = boxes.reduce((low, box) => Vector3.Minimize(low, box.minimumWorld), boxes[0]!.minimumWorld);
    const max = boxes.reduce((high, box) => Vector3.Maximize(high, box.maximumWorld), boxes[0]!.maximumWorld);
    return new Vector3((min.x + max.x) / 2, min.y, (min.z + max.z) / 2);
}

/** Imports a prop model as one hidden base mesh: merged, transform baked, centered with its base on the floor. */
async function importBaseMesh(scene: Scene, model: string): Promise<Mesh> {
    const path = `${ASSET_ROOT}${model}`;
    const slash = path.lastIndexOf('/');
    const result = await SceneLoader.ImportMeshAsync('', path.slice(0, slash + 1), path.slice(slash + 1), scene);
    const geometry = result.meshes.filter((mesh): mesh is Mesh => mesh instanceof Mesh && mesh.getTotalVertices() > 0);
    if (geometry.length === 0) {
        throw new Error(`No geometry in ${model}`);
    }
    // Light anchors, read in the imported scene before the geometry is merged and moved.
    const markers = [...(result.transformNodes ?? []), ...result.meshes]
        .filter((node) => isLightMarkerName(node.name))
        .map((node) => node.computeWorldMatrix(true).getTranslation());
    const fireBase = findFireBase(geometry);
    const root = result.meshes.find((mesh) => mesh.name === '__root__');
    const rootWorld = root ? root.computeWorldMatrix(true).clone() : Matrix.Identity();
    let merged: Mesh | null;
    if (geometry.length === 1) {
        merged = geometry[0]!;
        merged.computeWorldMatrix(true);
        merged.bakeTransformIntoVertices(merged.getWorldMatrix());
        merged.parent = null;
    } else {
        // Merging bakes each source mesh's world transform (the glTF root's handedness flip included).
        merged = Mesh.MergeMeshes(geometry, true, true, undefined, true, true);
    }
    if (!merged) {
        throw new Error(`Could not merge ${model}`);
    }
    for (const leftover of [...result.meshes, ...(result.transformNodes ?? [])]) {
        if (leftover !== merged && !leftover.isDisposed()) {
            leftover.dispose();
        }
    }

    merged.position.setAll(0);
    merged.rotationQuaternion = null;
    merged.rotation.setAll(0);
    merged.scaling.setAll(1);
    merged.refreshBoundingInfo();
    const box = merged.getBoundingInfo().boundingBox;
    const center = box.minimum.add(box.maximum).scale(0.5);
    merged.position = new Vector3(-center.x, -box.minimum.y, -center.z);
    // The same shift sets the light anchors on the floor with the model.
    const shift = merged.position.clone();
    modelAnchors.set(merged, {
        markers: markers.map((point) => point.add(shift)),
        fireBase: fireBase?.add(shift) ?? null,
        fromModel: rootWorld.multiply(Matrix.Translation(shift.x, shift.y, shift.z)),
    });
    merged.bakeCurrentTransformIntoVertices();
    merged.refreshBoundingInfo();
    merged.isVisible = false;
    merged.isPickable = false;
    merged.name = `prop-base-${model}`;
    return merged;
}
